import Loader from "components/Common/Loader";
import { TV_SAVE_LOAD_CHARTS_KEY } from "config/localStorage";
import { isChartAvailableForToken } from "config/tokens";
import { SUPPORTED_RESOLUTIONS_V1, SUPPORTED_RESOLUTIONS_V2 } from "config/tradingview";
import { useSettings } from "context/SettingsContext/SettingsContextProvider";
import { useOracleKeeperFetcher } from "domain/synthetics/tokens/useOracleKeeperFetcher";
import { TokenPrices } from "domain/tokens";
import { DataFeed } from "domain/tradingview/DataFeed";
import { getObjectKeyFromValue, getSymbolName } from "domain/tradingview/utils";
import { useTradePageVersion } from "lib/useTradePageVersion";
import { CSSProperties, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLatest, useLocalStorage, useMedia } from "react-use";
import type {
  ChartData,
  ChartingLibraryWidgetOptions,
  IChartingLibraryWidget,
  IPositionLineAdapter,
  ResolutionString,
} from "../../charting_library";
import { SaveLoadAdapter } from "./SaveLoadAdapter";
import { defaultChartProps, disabledFeaturesOnMobile } from "./constants";

export type ChartLine = {
  price: number;
  title: string;
};

type Props = {
  chainId: number;
  chartLines: ChartLine[];
  period: string;
  setPeriod: (period: string) => void;
  chartToken:
    | ({
        symbol: string;
      } & TokenPrices)
    | { symbol: string };
  supportedResolutions: typeof SUPPORTED_RESOLUTIONS_V1 | typeof SUPPORTED_RESOLUTIONS_V2;
  visualMultiplier?: number;
  setIsCandlesLoaded?: (isCandlesLoaded: boolean) => void;
};

export default function TVChartContainer({
  chartToken,
  chainId,
  chartLines,
  period,
  setPeriod,
  supportedResolutions,
  visualMultiplier,
  setIsCandlesLoaded,
}: Props) {
  const { shouldShowPositionLines } = useSettings();
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const tvWidgetRef = useRef<IChartingLibraryWidget | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [chartReady, setChartReady] = useState(false);
  const [chartDataLoading, setChartDataLoading] = useState(true);
  const [tvCharts, setTvCharts] = useLocalStorage<ChartData[] | undefined>(TV_SAVE_LOAD_CHARTS_KEY, []);

  const [tradePageVersion] = useTradePageVersion();

  const oracleKeeperFetcher = useOracleKeeperFetcher(chainId);

  const [datafeed, setDatafeed] = useState<DataFeed | null>(null);

  useEffect(() => {
    const newDatafeed = new DataFeed(chainId, oracleKeeperFetcher, tradePageVersion);
    if (setIsCandlesLoaded) {
      newDatafeed.addEventListener("candlesLoad.success", (event: Event) => {
        const isFirstDraw = (event as CustomEvent).detail.isFirstTimeLoad;
        if (isFirstDraw) {
          setIsCandlesLoaded(true);
        }
      });
    }
    setDatafeed((prev) => {
      if (prev) {
        prev.destroy();
      }
      return newDatafeed;
    });
  }, [chainId, oracleKeeperFetcher, setIsCandlesLoaded, tradePageVersion]);

  const isMobile = useMedia("(max-width: 550px)");
  const symbolRef = useRef(chartToken.symbol);

  const drawLineOnChart = useCallback(
    (title: string, price: number) => {
      if (chartReady && tvWidgetRef.current?.activeChart?.().dataReady()) {
        const chart = tvWidgetRef.current.activeChart();
        const positionLine = chart.createPositionLine({ disableUndo: true });

        return positionLine
          .setText(title)
          .setPrice(price)
          .setQuantity("")
          .setLineStyle(1)
          .setLineLength(1)
          .setBodyFont(`normal 12pt "Relative", sans-serif`)
          .setBodyTextColor("#fff")
          .setLineColor("#d4a843")
          .setBodyBackgroundColor("#13141f")
          .setBodyBorderColor("#d4a843");
      }
    },
    [chartReady]
  );

  useEffect(
    function updateLines() {
      const lines: (IPositionLineAdapter | undefined)[] = [];
      if (shouldShowPositionLines) {
        chartLines.forEach((order) => {
          lines.push(drawLineOnChart(order.title, order.price));
        });
      }
      return () => {
        lines.forEach((line) => line?.remove());
      };
    },
    [chartLines, shouldShowPositionLines, drawLineOnChart]
  );

  useEffect(() => {
    if (
      chartReady &&
      tvWidgetRef.current &&
      chartToken.symbol &&
      isChartAvailableForToken(chainId, chartToken.symbol)
    ) {
      tvWidgetRef.current.setSymbol(
        getSymbolName(chartToken.symbol, visualMultiplier),
        tvWidgetRef.current.activeChart().resolution(),
        async () => {
          const priceScale = tvWidgetRef.current?.activeChart().getPanes().at(0)?.getMainSourcePriceScale();
          if (priceScale) {
            priceScale.setAutoScale(true);
          }
        }
      );
    }
  }, [chainId, chartReady, chartToken.symbol, visualMultiplier]);

  const lastPeriod = useLatest(period);
  const lastSupportedResolutions = useLatest(supportedResolutions);

  useLayoutEffect(() => {
    if (symbolRef.current) {
      datafeed?.prefetchBars(symbolRef.current);
    }
  }, [datafeed, lastPeriod, lastSupportedResolutions]);

  useEffect(() => {
    if (!datafeed) return;

    let cancelled = false;
    let readyCheckTimer: ReturnType<typeof setInterval> | null = null;

    function initWidget() {
      if (cancelled || !chartContainerRef.current) return;

      const widgetOptions: ChartingLibraryWidgetOptions = {
        debug: false,
        symbol: symbolRef.current && getSymbolName(symbolRef.current, visualMultiplier), // Using ref to avoid unnecessary re-renders on symbol change and still have access to the latest symbol
        datafeed,
        theme: defaultChartProps.theme,
        container: chartContainerRef.current!,
        library_path: defaultChartProps.library_path,
        locale: defaultChartProps.locale,
        loading_screen: defaultChartProps.loading_screen,
        enabled_features: defaultChartProps.enabled_features,
        disabled_features: isMobile
          ? defaultChartProps.disabled_features.concat(disabledFeaturesOnMobile)
          : defaultChartProps.disabled_features,
        client_id: defaultChartProps.client_id,
        user_id: defaultChartProps.user_id,
        fullscreen: defaultChartProps.fullscreen,
        autosize: defaultChartProps.autosize,
        custom_css_url: defaultChartProps.custom_css_url,
        overrides: defaultChartProps.overrides,
        interval: getObjectKeyFromValue(period, supportedResolutions) as ResolutionString,
        favorites: { ...defaultChartProps.favorites, intervals: Object.keys(supportedResolutions) as ResolutionString[] },
        custom_formatters: defaultChartProps.custom_formatters,
        load_last_chart: true,
        auto_save_delay: 1,
        save_load_adapter: new SaveLoadAdapter(tvCharts, setTvCharts, tradePageVersion),
      };
      tvWidgetRef.current = new window.TradingView.widget(widgetOptions);

      tvWidgetRef.current!.onChartReady(function () {
        if (cancelled) return;
      setChartReady(true);

      // The autosize widget measures its container height once at init, which
      // can be wrong before the layout settles (desktop: chart too short with an
      // empty gap; mobile: content taller than the box → an internal scrollbar).
      // Re-fit the chart whenever the container's size actually changes.
      try {
        const el = chartContainerRef.current;
        if (el && typeof ResizeObserver !== "undefined") {
          let raf = 0;
          const refit = () => {
            // The chart renders inside a same-origin iframe that has its own
            // window. TradingView's autosize listener is bound to THAT window,
            // so a resize dispatched on the parent never reaches it — it keeps
            // the stale measurement it took at init and draws short, leaving
            // dead space between the chart and the positions list. Poke the
            // iframe's own window so it re-lays out to fill the box exactly.
            try {
              const frame = el.querySelector("iframe") as HTMLIFrameElement | null;
              frame?.contentWindow?.dispatchEvent(new Event("resize"));
            } catch (_) { /* best-effort */ }
            window.dispatchEvent(new Event("resize"));
            try {
              tvWidgetRef.current?.activeChart()?.getPanes?.()?.[0]?.getMainSourcePriceScale?.()?.setAutoScale?.(true);
            } catch (_) { /* best-effort */ }
          };
          resizeObserverRef.current = new ResizeObserver(() => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(refit);
          });
          resizeObserverRef.current.observe(el);
          // The box only reaches its final height after first paint, so the
          // measurement taken at init is almost always the stale one.
          raf = requestAnimationFrame(refit);
        }
      } catch (_) { /* best-effort */ }

      // Re-enable vertical auto-scale so candles fit the visible band on load
      // (otherwise they can sit below the fold and look like a scroll).
      try {
        tvWidgetRef.current?.activeChart()?.getPanes?.()?.[0]?.getMainSourcePriceScale?.()?.setAutoScale?.(true);
      } catch (_) {
        /* best-effort */
      }

      const savedPeriod = tvWidgetRef.current?.activeChart().resolution();
      const preferredPeriod = getObjectKeyFromValue(period, supportedResolutions) as ResolutionString;

      if (savedPeriod && savedPeriod !== preferredPeriod) {
        tvWidgetRef.current?.activeChart().setResolution(preferredPeriod);
      }

      tvWidgetRef.current
        ?.activeChart()
        .onIntervalChanged()
        .subscribe(null, (interval) => {
          if (supportedResolutions[interval]) {
            const period = supportedResolutions[interval];
            setPeriod(period);
            tvWidgetRef.current?.saveChartToServer(undefined, undefined, {
              chartName: `gmx-chart-v${tradePageVersion}`,
            });

            const priceScale = tvWidgetRef.current?.activeChart().getPanes().at(0)?.getMainSourcePriceScale();
            if (priceScale) {
              priceScale.setAutoScale(true);
            }
          }
        });

      tvWidgetRef.current?.subscribe("onAutoSaveNeeded", () => {
        tvWidgetRef.current?.saveChartToServer(undefined, undefined, {
          chartName: `gmx-chart-v${tradePageVersion}`,
        });
      });

      tvWidgetRef.current?.activeChart().dataReady(() => {
        setChartDataLoading(false);
        // Auto-fit price scale so candles fill the visible area on every load
        const priceScale = tvWidgetRef.current?.activeChart().getPanes().at(0)?.getMainSourcePriceScale();
        if (priceScale) {
          priceScale.setAutoScale(true);
        }
      });
      });
    }

    // TradingView library is loaded async — poll until window.TradingView is ready.
    if ((window as any).TradingView?.widget) {
      initWidget();
    } else {
      readyCheckTimer = setInterval(() => {
        if ((window as any).TradingView?.widget) {
          clearInterval(readyCheckTimer!);
          readyCheckTimer = null;
          initWidget();
        }
      }, 100);
    }

    return () => {
      cancelled = true;
      if (readyCheckTimer !== null) {
        clearInterval(readyCheckTimer);
        readyCheckTimer = null;
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (tvWidgetRef.current) {
        tvWidgetRef.current.remove();
        tvWidgetRef.current = null;
        setChartReady(false);
        setChartDataLoading(true);
      }
    };
    // We don't want to re-initialize the chart when the symbol changes. This will make the chart flicker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId, datafeed]);

  const style = useMemo<CSSProperties>(
    () => ({ visibility: !chartDataLoading ? "visible" : "hidden" }),
    [chartDataLoading]
  );

  return (
    <div className="ExchangeChart-error">
      {chartDataLoading && <Loader />}
      <div style={style} ref={chartContainerRef} className="TVChartContainer ExchangeChart-bottom-content" />
    </div>
  );
}
